import { DEVICES } from '../../constants/breakpoints';
import { DEFAULT_STYLE_VALUES } from '../../constants/defaultStyleValues';

export const updateElementStyles = ({
  id,
  propertyName, 
  propertyValue,
  updatedStyles,
  device,
  elements,
  selectedElement,
  setElements,
  setSelectedElement,
  classStylesRegistry,
  setClassStylesRegistry,
  combinedClassStylesRegistry,
  setCombinedClassStylesRegistry,
  currentPageId,
  setPages,
  getNextClassNumber,
  getDefaultStyles,
  addToGlobalClassNames,
  getStateStyles,
  getCascadedStyles
}) => {
  // Find the current element
  const currentElement = elements.find(el => el.id === id);
  // Get current state from selected element
  const currentState = selectedElement?.currentState || 'default';

  // If element doesn't have a className, create and apply a new one
  if (!currentElement?.className) {
    // Get element type and create new class name
    const elementType = currentElement.type.toLowerCase();
    const nextNumber = getNextClassNumber(elementType);
    const newClassName = `${elementType}-${nextNumber}`;

    // Get default styles for this element type
    const defaultStyles = getDefaultStyles(elementType,currentElement);

    // Add to global class names
    addToGlobalClassNames(newClassName);

    // Update element with new class name and combine default styles with new style
    setElements(prevElements =>
      prevElements.map(element =>
        element.id === id
          ? { 
              ...element, 
              className: newClassName,
              styles: {
                ...defaultStyles,
                [propertyName]: propertyValue  // New style overrides default if same property
              }
            }
          : element
      )
    );

    // Update selected element if it's the current element
    if (selectedElement && selectedElement.id === id) {
      setSelectedElement(prev => ({
        ...prev,
        className: newClassName,
        styles: {
          ...defaultStyles,
          [propertyName]: propertyValue  // New style overrides default if same property
        }
      }));
    }

    // Initialize class in registry with default styles plus the new style
    setClassStylesRegistry(prev => {
      const newRegistry = { ...prev };
      const deviceMap = new Map(prev[device]);
      deviceMap.set(newClassName, {
        ...defaultStyles,
        [propertyName]: propertyValue
      });
      newRegistry[device] = deviceMap;
      return newRegistry;
    });

    // Update page's class registry with default styles plus the new style
    setPages(prevPages => 
      prevPages.map(page => {
        if (page.id === currentPageId) {
          const updatedClassStylesRegistry = { ...page.classStylesRegistry };
          const deviceRegistry = new Map(updatedClassStylesRegistry[device]);
          deviceRegistry.set(newClassName, {
            ...defaultStyles,
            [propertyName]: propertyValue
          });
          updatedClassStylesRegistry[device] = deviceRegistry;

          return {
            ...page,
            classStylesRegistry: updatedClassStylesRegistry
          };
        }
        return page;
      })
    );

    return;
  }

  // If the element has classes, update the appropriate registry
  const element = elements.find(el => el.id === id);
  if (element?.className) {
    const classNames = element.className.split(' ');
    
    if (classNames.length === 1) {
      const baseClassName = classNames[0];
      if (baseClassName) {
        const classNameToUpdate = currentState !== 'default' 
          ? `${baseClassName}--${currentState}`  
          : baseClassName;

        setClassStylesRegistry(prev => {
          const newRegistry = {
            [DEVICES.DESKTOP]: new Map(prev[DEVICES.DESKTOP]),
            [DEVICES.TABLET]: new Map(prev[DEVICES.TABLET]),
            [DEVICES.MOBILE]: new Map(prev[DEVICES.MOBILE])
          };

          // If we're in a non-default state, check if base class has the property
          if (currentState !== 'default') {
            // Get the existing base styles without modifying them
            const existingBaseStyles = prev[device]?.get(baseClassName) || {};
            
            // Only add value if property doesn't exist in base styles
            if (!(propertyName in existingBaseStyles)) {
              let valueToAdd;
              const deviceOrder = [DEVICES.MOBILE, DEVICES.TABLET, DEVICES.DESKTOP];
              const currentDeviceIndex = deviceOrder.indexOf(device);
              let foundInParent = false;

              // Check parent breakpoints for the property
              for (let i = currentDeviceIndex + 1; i < deviceOrder.length; i++) {
                const parentDevice = deviceOrder[i];
                const parentStyles = prev[parentDevice]?.get(baseClassName) || {};
                
                if (propertyName in parentStyles) {
                  valueToAdd = parentStyles[propertyName];
                  foundInParent = true;
                  break;
                }
              }

              // If not found in any parent breakpoint, use default value
              if (!foundInParent) {
                valueToAdd = DEFAULT_STYLE_VALUES[propertyName];
              }

              if (valueToAdd !== undefined) {
                // Create new base styles with the found value
                const newBaseStyles = {
                  ...existingBaseStyles,
                  [propertyName]: valueToAdd
                };
                
                // Set base styles only if property didn't exist before
                newRegistry[device].set(baseClassName, newBaseStyles);
              }
            } else {
              // If property exists, preserve the existing base styles exactly as they are
              newRegistry[device].set(baseClassName, {...existingBaseStyles});
            }
          }

          // Only update the state-specific class
          const deviceRegistry = newRegistry[device];
          if (currentState !== 'default') {
            const stateStyles = deviceRegistry.get(classNameToUpdate) || {};
            const updatedStateStyles = {
              ...stateStyles,
              [propertyName]: propertyValue
            };
            deviceRegistry.set(classNameToUpdate, updatedStateStyles);
          } else {
            const baseStyles = deviceRegistry.get(classNameToUpdate) || {};
            const updatedStyles = {
              ...baseStyles,
              [propertyName]: propertyValue
            };
            deviceRegistry.set(classNameToUpdate, updatedStyles);
          }

          // If we're in a non-default state, check smaller breakpoints
          if (currentState !== 'default') {
            const deviceOrder = [DEVICES.DESKTOP, DEVICES.TABLET, DEVICES.MOBILE];
            const currentDeviceIndex = deviceOrder.indexOf(device);

            // Get the current value for this property across all devices
            const deviceValues = deviceOrder.map(d => {
              const styles = prev[d]?.get(classNameToUpdate) || {};
              return styles[propertyName];
            });

            // Update smaller breakpoints if they had the same value
            for (let i = currentDeviceIndex + 1; i < deviceOrder.length; i++) {
              const smallerDevice = deviceOrder[i];
              const smallerDeviceStyles = prev[smallerDevice]?.get(classNameToUpdate) || {};
              
              // If the smaller device had the same value as the previous device
              if (deviceValues[i - 1] === deviceValues[i]) {
                newRegistry[smallerDevice].set(classNameToUpdate, {
                  ...smallerDeviceStyles,
                  [propertyName]: propertyValue
                });
              }
            }
          }

          return newRegistry;
        });

        // Update page's class registry similarly
        setPages(prevPages => 
          prevPages.map(page => {
            if (page.id === currentPageId) {
              const updatedClassStylesRegistry = {
                [DEVICES.DESKTOP]: new Map(page.classStylesRegistry[DEVICES.DESKTOP]),
                [DEVICES.TABLET]: new Map(page.classStylesRegistry[DEVICES.TABLET]),
                [DEVICES.MOBILE]: new Map(page.classStylesRegistry[DEVICES.MOBILE])
              };

              // If we're in a non-default state, check if base class has the property
              if (currentState !== 'default') {
                const baseStyles = page.classStylesRegistry[device]?.get(baseClassName) || {};
                
                // Only add value if property doesn't exist in base styles
                if (!(propertyName in baseStyles)) {
                  let valueToAdd;
                  const deviceOrder = [DEVICES.MOBILE, DEVICES.TABLET, DEVICES.DESKTOP];
                  const currentDeviceIndex = deviceOrder.indexOf(device);
                  let foundInParent = false;

                  // Check parent breakpoints for the property
                  for (let i = currentDeviceIndex + 1; i < deviceOrder.length; i++) {
                    const parentDevice = deviceOrder[i];
                    const parentStyles = page.classStylesRegistry[parentDevice]?.get(baseClassName) || {};
                    
                    if (propertyName in parentStyles) {
                      valueToAdd = parentStyles[propertyName];
                      foundInParent = true;
                      break;
                    }
                  }

                  // If not found in any parent breakpoint, use default value
                  if (!foundInParent) {
                    valueToAdd = DEFAULT_STYLE_VALUES[propertyName];
                  }

                  if (valueToAdd !== undefined) {
                    // Create new base styles with the found value
                    const updatedBaseStyles = {
                      ...baseStyles,
                      [propertyName]: valueToAdd
                    };
                    // Set it once and don't update it again
                    updatedClassStylesRegistry[device].set(baseClassName, updatedBaseStyles);
                  }
                }
              }

              // Update only the state class with new value
              const deviceRegistry = updatedClassStylesRegistry[device];
              const existingStyles = deviceRegistry.get(classNameToUpdate) || {};
              const updatedDeviceStyles = {
                ...existingStyles,
                [propertyName]: propertyValue
              };
              deviceRegistry.set(classNameToUpdate, updatedDeviceStyles);

              // Apply same cascading logic for pages
              if (currentState !== 'default') {
                const deviceOrder = [DEVICES.DESKTOP, DEVICES.TABLET, DEVICES.MOBILE];
                const currentDeviceIndex = deviceOrder.indexOf(device);

                const deviceValues = deviceOrder.map(d => {
                  const styles = page.classStylesRegistry[d]?.get(classNameToUpdate) || {};
                  return styles[propertyName];
                });

                for (let i = currentDeviceIndex + 1; i < deviceOrder.length; i++) {
                  const smallerDevice = deviceOrder[i];
                  const smallerDeviceMap = new Map(updatedClassStylesRegistry[smallerDevice]);
                  const smallerDeviceStyles = smallerDeviceMap.get(classNameToUpdate) || {};
                  
                  if (deviceValues[i - 1] === deviceValues[i]) {
                    smallerDeviceMap.set(classNameToUpdate, {
                      ...smallerDeviceStyles,
                      [propertyName]: propertyValue
                    });
                    updatedClassStylesRegistry[smallerDevice] = smallerDeviceMap;
                  }
                }
              }

              return {
                ...page,
                classStylesRegistry: updatedClassStylesRegistry
              };
            }
            return page;
          })
        );

        // Rest of the existing code for updating selectedElement...
        if (selectedElement && selectedElement.id === id) {
          // Get all styles for the current state
          const combinedStyles = {};
          
          // First get cascaded styles from all breakpoints
          const cascadedStyles = getCascadedStyles(baseClassName, device);
          Object.assign(combinedStyles, cascadedStyles);

          // Then get base styles for current state and device
          const baseStyles = getStateStyles(baseClassName, 'default', device);
          Object.assign(combinedStyles, baseStyles);

          // If in a non-default state, overlay state-specific styles
          if (currentState !== 'default') {
            const stateStyles = getStateStyles(baseClassName, currentState, device);
            Object.assign(combinedStyles, stateStyles);
          }

          // Finally add the new style
          combinedStyles[propertyName] = propertyValue;        
          setSelectedElement(prev => ({
            ...prev,
            styles: combinedStyles
          }));


          //Find elements with the same class name and update their styles
        setElements(prevElements =>
          prevElements.map(element => {
            if (element.className === baseClassName) {
              return {
                ...element, 
                styles: combinedStyles
              };
            }
            return element;
          })
        );
        }
      }
    }
    // Handle multiple classes case
    else if (classNames.length > 1) {
      // Create combined base class name using +
      const combinedBaseClassName = classNames.join('+');
      
      // Create state-specific combined class name if in a state
      const classNameToUpdate = currentState !== 'default' 
        ? `${combinedBaseClassName}--${currentState}`  
        : combinedBaseClassName;

      setCombinedClassStylesRegistry(prev => {
        const newRegistry = {
          [DEVICES.DESKTOP]: new Map(prev[DEVICES.DESKTOP]),
          [DEVICES.TABLET]: new Map(prev[DEVICES.TABLET]),
          [DEVICES.MOBILE]: new Map(prev[DEVICES.MOBILE])
        };

        // If we're in a non-default state, check if base combined class has the property
        if (currentState !== 'default') {
          const existingBaseStyles = prev[device]?.get(combinedBaseClassName) || {};
          
          // Only add value if property doesn't exist in base styles
          if (!(propertyName in existingBaseStyles)) {
            let valueToAdd;
            const deviceOrder = [DEVICES.MOBILE, DEVICES.TABLET, DEVICES.DESKTOP];
            const currentDeviceIndex = deviceOrder.indexOf(device);
            let foundInParent = false;

            // Check parent breakpoints for the property
            for (let i = currentDeviceIndex + 1; i < deviceOrder.length; i++) {
              const parentDevice = deviceOrder[i];
              const parentStyles = prev[parentDevice]?.get(combinedBaseClassName) || {};
              
              if (propertyName in parentStyles) {
                valueToAdd = parentStyles[propertyName];
                foundInParent = true;
                break;
              }
            }

            // If not found in any parent breakpoint, use default value
            if (!foundInParent) {
              valueToAdd = DEFAULT_STYLE_VALUES[propertyName];
            }

            if (valueToAdd !== undefined) {
              // Create new base styles with the found value
              const newBaseStyles = {
                ...existingBaseStyles,
                [propertyName]: valueToAdd
              };
              
              // Set base styles only if property didn't exist before
              newRegistry[device].set(combinedBaseClassName, newBaseStyles);
            }
          }

          // Update the state-specific combined class for current and smaller breakpoints
          const deviceOrder = [DEVICES.DESKTOP, DEVICES.TABLET, DEVICES.MOBILE];
          const currentDeviceIndex = deviceOrder.indexOf(device);

          // Get the current value for this property across all devices
          const deviceValues = deviceOrder.map(d => {
            const styles = prev[d]?.get(classNameToUpdate) || {};
            return styles[propertyName];
          });

          // Update current and smaller breakpoints
          for (let i = currentDeviceIndex; i < deviceOrder.length; i++) {
            const targetDevice = deviceOrder[i];
            const deviceStyles = prev[targetDevice]?.get(classNameToUpdate) || {};
            
            // If this is the current device or if the previous device had the same value
            if (i === currentDeviceIndex || deviceValues[i - 1] === deviceValues[i]) {
              newRegistry[targetDevice].set(classNameToUpdate, {
                ...deviceStyles,
                [propertyName]: propertyValue
              });
            }
          }
        } else {
          // Update base styles for current device
          const deviceStyles = prev[device]?.get(classNameToUpdate) || {};
          newRegistry[device].set(classNameToUpdate, {
            ...deviceStyles,
            [propertyName]: propertyValue
          });
        }

        return newRegistry;
      });

      // Update page's combinedClassStylesRegistry similarly
      setPages(prevPages => 
        prevPages.map(page => {
          if (page.id === currentPageId) {
            const updatedCombinedClassStylesRegistry = {
              [DEVICES.DESKTOP]: new Map(page.combinedClassStylesRegistry[DEVICES.DESKTOP]),
              [DEVICES.TABLET]: new Map(page.combinedClassStylesRegistry[DEVICES.TABLET]),
              [DEVICES.MOBILE]: new Map(page.combinedClassStylesRegistry[DEVICES.MOBILE])
            };

            // If we're in a non-default state, check if base combined class has the property
            if (currentState !== 'default') {
              const baseStyles = page.combinedClassStylesRegistry[device]?.get(combinedBaseClassName) || {};
              
              // Only add value if property doesn't exist in base styles
              if (!(propertyName in baseStyles)) {
                let valueToAdd;
                const deviceOrder = [DEVICES.MOBILE, DEVICES.TABLET, DEVICES.DESKTOP];
                const currentDeviceIndex = deviceOrder.indexOf(device);
                let foundInParent = false;

                // Check parent breakpoints for the property
                for (let i = currentDeviceIndex + 1; i < deviceOrder.length; i++) {
                  const parentDevice = deviceOrder[i];
                  const parentStyles = page.combinedClassStylesRegistry[parentDevice]?.get(combinedBaseClassName) || {};
                  
                  if (propertyName in parentStyles) {
                    valueToAdd = parentStyles[propertyName];
                    foundInParent = true;
                    break;
                  }
                }

                // If not found in any parent breakpoint, use default value
                if (!foundInParent) {
                  valueToAdd = DEFAULT_STYLE_VALUES[propertyName];
                }

                if (valueToAdd !== undefined) {
                  // Create new base styles with the found value
                  const updatedBaseStyles = {
                    ...baseStyles,
                    [propertyName]: valueToAdd
                  };
                  // Set it once and don't update it again
                  updatedCombinedClassStylesRegistry[device].set(combinedBaseClassName, updatedBaseStyles);
                }
              }
            }

            // Update the state-specific combined class with new value
            const deviceRegistry = updatedCombinedClassStylesRegistry[device];
            const existingStyles = deviceRegistry.get(classNameToUpdate) || {};
            const updatedDeviceStyles = {
              ...existingStyles,
              [propertyName]: propertyValue
            };
            deviceRegistry.set(classNameToUpdate, updatedDeviceStyles);

            // Apply same cascading logic for pages
            if (currentState !== 'default') {
              const deviceOrder = [DEVICES.DESKTOP, DEVICES.TABLET, DEVICES.MOBILE];
              const currentDeviceIndex = deviceOrder.indexOf(device);

              const deviceValues = deviceOrder.map(d => {
                const styles = page.combinedClassStylesRegistry[d]?.get(classNameToUpdate) || {};
                return styles[propertyName];
              });

              for (let i = currentDeviceIndex + 1; i < deviceOrder.length; i++) {
                const smallerDevice = deviceOrder[i];
                const smallerDeviceMap = new Map(updatedCombinedClassStylesRegistry[smallerDevice]);
                const smallerDeviceStyles = smallerDeviceMap.get(classNameToUpdate) || {};
                
                if (deviceValues[i - 1] === deviceValues[i]) {
                  smallerDeviceMap.set(classNameToUpdate, {
                    ...smallerDeviceStyles,
                    [propertyName]: propertyValue
                  });
                  updatedCombinedClassStylesRegistry[smallerDevice] = smallerDeviceMap;
                }
              }
            }

            return {
              ...page,
              combinedClassStylesRegistry: updatedCombinedClassStylesRegistry
            };
          }
          return page;
        })
      );

      // Update elements array
      setElements(prevElements =>
        prevElements.map(element => {
          if (element.id === id) {
            const combinedStyles = {};
            
            // First get cascaded styles from all breakpoints
            const cascadedStyles = getCascadedStyles(combinedBaseClassName, device);
            Object.assign(combinedStyles, cascadedStyles);

            // Then get base styles for current state and device
            const baseStyles = getStateStyles(combinedBaseClassName, 'default', device);
            Object.assign(combinedStyles, baseStyles);

            // If in a non-default state, overlay state-specific styles
            if (currentState !== 'default') {
              const stateStyles = getStateStyles(combinedBaseClassName, currentState, device);
              Object.assign(combinedStyles, stateStyles);
            }

            // Finally add the new style
            combinedStyles[propertyName] = propertyValue;

            return {
              ...element,
              styles: combinedStyles
            };
          }
          return element;
        })
      );

      // Update selected element
      if (selectedElement && selectedElement.id === id) {
        const combinedStyles = {};
        
        // First get cascaded styles from all breakpoints
        const cascadedStyles = getCascadedStyles(combinedBaseClassName, device);
        Object.assign(combinedStyles, cascadedStyles);

        // Then get base styles for current state and device
        const baseStyles = getStateStyles(combinedBaseClassName, 'default', device);
        Object.assign(combinedStyles, baseStyles);

        // If in a non-default state, overlay state-specific styles
        if (currentState !== 'default') {
          const stateStyles = getStateStyles(combinedBaseClassName, currentState, device);
          Object.assign(combinedStyles, stateStyles);
        }

        // Finally add the new style
        combinedStyles[propertyName] = propertyValue;

        setSelectedElement(prev => ({
          ...prev,
          styles: combinedStyles
        }));
      }
    }
  }
}; 